import {
  ICellRendererParams,
  GridApi,
  RowNode,
  ColDef,
  ColumnApi,
  ICellRendererComp,
  RowRenderer,
  Column,
  ValueGetterParams,
} from 'ag-grid-community';
import {
  get,
  isEmpty,
  some,
  first,
  merge,
  isEqual,
  size,
  head,
  uniq,
  map,
  every,
  filter,
  isFunction,
  isString,
} from 'lodash';
import { getArraysOfScalarsComparator } from '@shared/utils/common-utils';
import { Primitive } from '@geniuspos/ui-shared/modules/xdm-shared-components/definitions/types/common.type';
import {
  GetAllRowsOptions,
  GridExpandedState,
} from '@app/shared/types/ui/xux-grid/xux-grid.type';

export const getDisplayedItemsIds = (gridApi: GridApi): string[] => {
  if (!gridApi) return [];

  const displayedItemsIds = [];
  gridApi.forEachNodeAfterFilterAndSort((rowNode: RowNode) =>
    displayedItemsIds.push(rowNode.id),
  );

  return displayedItemsIds;
};

export const getAllRows = <T = any>(
  gridApi: GridApi,
  options?: GetAllRowsOptions<T>,
): T[] => {
  const {
    selector = () => true,
    valueSelector = (node) => node.data,
    withFilterAndSort = false,
  } = options || {};

  const loopFunc = withFilterAndSort
    ? get(gridApi, 'forEachNodeAfterFilterAndSort')
    : get(gridApi, 'forEachNode');

  const rowData = [];

  if (!isFunction(loopFunc)) {
    return rowData;
  }

  loopFunc.call(gridApi, (node) => {
    if (selector(node)) {
      rowData.push(valueSelector(node));
    }
  });

  return rowData;
};

export const getAllRowsAfterFilterAndSort = (gridApi: GridApi): RowNode[] => {
  const allNode: RowNode[] = [];

  if (!gridApi) {
    return [];
  }

  gridApi.forEachNodeAfterFilterAndSort((node) => allNode.push(node));

  return allNode;
};

export const getDisplayedSelectedRows = (gridApi: GridApi): RowNode[] => {
  const allDisplayedSelectedRows = [];

  gridApi.forEachNodeAfterFilterAndSort((row: RowNode) => {
    if (row.isSelected()) {
      allDisplayedSelectedRows.push(row);
    }
  });

  return allDisplayedSelectedRows;
};

export const getValueForPinnedCell = (
  params: ICellRendererParams,
  gridApi: GridApi,
): any => {
  const field = get(params, 'column.colDef.field', '');
  const values: any[] = [];

  gridApi.forEachNodeAfterFilterAndSort((rowNode: RowNode) =>
    values.push(get(rowNode.data, field)),
  );

  return isEmpty(values) ||
    some(values, (val) => {
      const value = val === '' ? undefined : val;
      const firstEl = first(values) === '' ? undefined : first(values);

      return value !== firstEl;
    })
    ? null
    : first(values);
};

export const getValueForArrayPinnedCell = (
  params: ICellRendererParams,
  gridApi: GridApi,
): PinnedResult => {
  const field = get(params, 'column.colDef.field', '');

  const counter = getArraysOfScalarsComparator();
  gridApi.forEachNodeAfterFilterAndSort((rowNode: RowNode) => {
    counter.next(get(rowNode.data, field));
  });

  return counter.getResult();
};

/**
 * set column colDef
 *
 * @param gridApi target grid api
 * @param column target column colId(path in form-rules)
 * @param newColDef new colDef
 */
export const setColumnDef = (
  gridApi: GridApi,
  column: string,
  newColDef: ColDef,
): void => {
  const columnApi: ColumnApi = gridApi?.['columnController']?.columnApi;

  if (!columnApi) {
    return;
  }

  const targetColumnDef = gridApi.getColumnDef(column);

  if (!targetColumnDef) {
    return;
  }

  const mergedColDef = merge({}, targetColumnDef, newColDef);

  // just return when old value is equal to new or column is not visible
  if (
    isEqual(targetColumnDef, mergedColDef) ||
    !columnApi.getColumn(column).isVisible()
  ) {
    return;
  }

  merge(targetColumnDef, newColDef);

  gridApi.refreshHeader();
};

export const getCellRendererInstance = (params: {
  rowIndex: number;
  columnId: string;
  gridApi: GridApi;
}): ICellRendererComp => {
  const { rowIndex, columnId, gridApi } = params;

  const rowNode = gridApi.getDisplayedRowAtIndex(rowIndex);

  if (!rowNode) {
    return null;
  }

  const rowRenderer: RowRenderer = rowNode['context'].getBean('rowRenderer');

  return get(rowRenderer, [
    'rowCompsByIndex',
    rowIndex,
    'cellComps',
    columnId,
    'cellRenderer',
  ]);
};

export const getCellRendererFrameworkInstance = (params: {
  rowIndex: number;
  column: string | Column;
  gridApi: GridApi;
}): any => {
  const { rowIndex, column, gridApi } = params;

  if (!gridApi) {
    return null;
  }

  const focusedCellRendererInstance = getCellRendererInstance({
    rowIndex,
    columnId: isString(column) ? column : column.getId(),
    gridApi,
  });

  return get(focusedCellRendererInstance, ['_frameworkComponentInstance']);
};

export const clearFocusedCellRenderer = (params: {
  gridApi: GridApi;
}): void => {
  if (!params?.gridApi) {
    return;
  }

  const curFocusPosition = params.gridApi.getFocusedCell();

  if (!curFocusPosition) {
    return;
  }

  const focusCellRendererInstance = getCellRendererInstance({
    gridApi: params.gridApi,
    rowIndex: curFocusPosition?.rowIndex,
    columnId: curFocusPosition?.column?.getColId(),
  });

  if (focusCellRendererInstance) {
    // need this to make sure trigger finally when click grid action bar
    queueMicrotask(() =>
      focusCellRendererInstance.getGui().querySelector('input')?.blur(),
    );
  }
};

export const getIsNodeExpanded = (node: RowNode): boolean => {
  if (!node.expanded) {
    return false;
  }

  const children = filter(node.childrenAfterFilter, (child) => child.group);

  if (isEmpty(children)) {
    return true;
  }

  return every(children, (child) => getIsNodeExpanded(child));
};

export const getCollapsedOrExpanded = (gridApi: GridApi): GridExpandedState => {
  if (!gridApi) {
    return GridExpandedState.None;
  }

  const allRows = getAllRows(gridApi, {
    selector: (rowNode) => rowNode.group,
    valueSelector: (rowNode) => rowNode,
  });

  const allRowExpandStates = uniq(
    map(allRows, (row) => getIsNodeExpanded(row)),
  );

  if (isEqual(size(allRowExpandStates), 1)) {
    return head(allRowExpandStates)
      ? GridExpandedState.Expanded
      : GridExpandedState.Collapsed;
  }

  return GridExpandedState.Varies;
};

/**
 * only for tree data mode
 *
 * @param params
 * @returns
 */
export const isLeaf = (
  params: ICellRendererParams | RowNode | ValueGetterParams,
): boolean => {
  return get(params, ['data', 'isLeaf'], false);
};

type PinnedResult = 'Varies' | Primitive[];
